package com.eladfinish.bible.contest.screens.chapterComplete.usecases

import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextRange
import com.eladfinish.bible.contest.common.settings.KeyValueSettings
import com.eladfinish.bible.contest.common.usecases.RemoveNikudAndTeamimAndMakafUseCase
import com.eladfinish.bible.contest.constants.ColorConstants.INDIGO_DARK
import com.eladfinish.bible.contest.constants.SharedPrefsConstants.KEY_ENABLE_LINE_LENGTH_ACCORDING_TO_WORD_LENGTH
import com.eladfinish.bible.contest.constants.SharedPrefsConstants.KEY_ENABLE_SPACES_BETWEEN_BLANKS_IN_CHAPTER_COMPLETE
import com.eladfinish.bible.contest.constants.StringConstants.COLON_SPLITTER
import com.eladfinish.bible.contest.constants.StringConstants.MAKAF
import com.eladfinish.bible.contest.constants.StringConstants.NO_BREAK_SPACE
import com.eladfinish.bible.contest.extensions.FONT_SERIF
import com.eladfinish.bible.contest.text.ClickAction
import com.eladfinish.bible.contest.text.SpanType
import com.eladfinish.bible.contest.text.SpannableStringKMP
import com.eladfinish.bible.contest.text.SpannedRange
import com.eladfinish.bible.contest.utils.RegexUtils.UNDERSCORES
import kotlinx.collections.immutable.toImmutableList

class BuildSavedCompletionOutputUseCase(
    private val settings: KeyValueSettings,
    private val removeNikudAndTeamimAndMakafUseCase: RemoveNikudAndTeamimAndMakafUseCase,
) {

    /**
     * Builds a saved-completion output from a partitioned chapter + blanks indexes.
     *
     * Assumptions (matches your legacy behavior as closely as possible):
     * - partitionedChapter is from PartitionChapterUseCase:
     *   verseParts[0][0] = otPasuk
     *   verseParts[1]    = reysha words
     *   verseParts[2]    = seyfa words
     * - blanksPerVerse[i] contains 1-based word indexes over (reysha + seyfa).
     * - If removeVerseLetter=true, shift index by -1 (legacy behavior).
     */
    @Suppress("KDocUnresolvedReference")
    operator fun invoke(
        partitionedChapter: List<List<List<String>>>,
        blanksPerVerse: List<List<Int>>,
        removeVerseLetter: Boolean,
        eachVerseInNewLine: Boolean,
    ): SpannableStringKMP {
        val out = StringBuilder()
        val spans = mutableListOf<SpannedRange>()

        val verseCount = partitionedChapter.size
        val safeBlanks = if (blanksPerVerse.size < verseCount) {
            blanksPerVerse + List(verseCount - blanksPerVerse.size) { emptyList() }
        } else {
            blanksPerVerse
        }

        for (verseIndex in 0 until verseCount) {
            val verseParts = partitionedChapter[verseIndex]

            val otPasuk = verseParts.getOrNull(0)?.getOrNull(0).orEmpty()
            val reyshaWords = verseParts.getOrNull(1).orEmpty()
            val seyfaWords = verseParts.getOrNull(2).orEmpty()
            val words = (reyshaWords + seyfaWords).toList()

            val blanksRaw = safeBlanks[verseIndex]
            val blankWordIndexes0Based = blanksRaw
                .asSequence()
                .mapNotNull { rawIndex ->
                    val shifted = if (removeVerseLetter) rawIndex - 1 else rawIndex

                    // We treat blanks as 1-based indexes over (reysha + seyfa).
                    // 0 / negative / out-of-range are ignored.
                    shifted.takeIf { it >= 1 && it <= words.size }?.let { it - 1 }
                }
                .toSet()

            if (otPasuk.isNotBlank()) {
                val start = out.length
                out.append(otPasuk)
                val end = out.length

                // Same as BuildContentByExposureTypeDomainUseCase.appendOtPasuk()
                spans += SpannedRange(
                    spanType = SpanType.RelativeSize(proportion = 0.5f),
                    textRange = TextRange(start = start, end = end),
                )

                out.append(NO_BREAK_SPACE)
            }

            for (wordIndex in words.indices) {
                val originWord = words[wordIndex]
                val endsWithMakaf = originWord.endsWith(MAKAF.toString())

                if (blankWordIndexes0Based.contains(wordIndex)) {
                    appendBlank(
                        out = out,
                        spans = spans,
                        originWord = originWord,
                        color = INDIGO_DARK,
                        typefaceFamily = FONT_SERIF,
                    )

                    // IMPORTANT: keep the legacy quirk so RevealBlankUseCase shift=1 stays correct
                    if (endsWithMakaf) {
                        out.append(" ")
                    }
                } else {
                    out.append(originWord)
                }

                if (!originWord.endsWith(MAKAF.toString()) && wordIndex != words.lastIndex) {
                    out.append(" ")
                }
            }

            out.append(COLON_SPLITTER)
            out.append(if (eachVerseInNewLine) "\n" else " ")
        }

        val raw = out.toString().trimEnd()

        return SpannableStringKMP(
            charSequence = raw,
            spannedRangeList = spans.toImmutableList(),
        )
    }

    /**
     * Same style as BuildContentByExposureTypeDomainUseCase.appendBlank():
     * - underline text based on replaceWordWithUnderline()
     * - spans: CustomTypeface + ForegroundColor + Clickable(RevealBlank)
     */
    private fun appendBlank(
        out: StringBuilder,
        spans: MutableList<SpannedRange>,
        originWord: String,
        color: Color,
        typefaceFamily: String,
    ) {
        val partOnlyLetters = removeNikudAndTeamimAndMakafUseCase(originWord)
        val underline = replaceWordWithUnderline(word = partOnlyLetters)

        val start = out.length
        out.append(underline)
        val end = out.length

        spans += SpannedRange(
            spanType = SpanType.CustomTypeface(
                family = typefaceFamily,
            ),
            textRange = TextRange(start = start, end = end),
        )

        spans += SpannedRange(
            spanType = SpanType.ForegroundColor(
                color = color,
            ),
            textRange = TextRange(start = start, end = end),
        )

        spans += SpannedRange(
            spanType = SpanType.Clickable(
                action = ClickAction.RevealBlank(
                    originWord = originWord,
                    endsWithMakaf = originWord.endsWith(suffix = MAKAF.toString()),
                ),
            ),
            textRange = TextRange(start = start, end = end),
        )
    }

    /**
     * Copied 1:1 in behavior from BuildContentByExposureTypeDomainUseCase.replaceWordWithUnderline()
     * (but without its internal constants leakage).
     */
    private fun replaceWordWithUnderline(
        word: String,
    ): String {
        val variableLength = settings.getBoolean(
            key = KEY_ENABLE_LINE_LENGTH_ACCORDING_TO_WORD_LENGTH,
            defaultValue = true,
        )
        val spaceBlanks = settings.getBoolean(
            key = KEY_ENABLE_SPACES_BETWEEN_BLANKS_IN_CHAPTER_COMPLETE,
            defaultValue = true,
        )

        val underscores = 4

        return if (spaceBlanks) {
            val underline = word.replace(
                regex = Regex("[^\\s$MAKAF]"),
                replacement = "_",
            )

            if (!variableLength) {
                val fixedBlanks = "_".repeat(underscores)
                underline.replace(
                    regex = UNDERSCORES.toRegex(),
                    replacement = fixedBlanks,
                )
            } else {
                underline
            }
        } else {
            if (variableLength) {
                "_".repeat(word.length)
            } else {
                val noSpaces = word.replace(
                    regex = Regex("[\\s$MAKAF]"),
                    replacement = "",
                )
                val numOfSpaces = word.length - noSpaces.length
                val numOfWords = numOfSpaces + 1
                val num = underscores * numOfWords + numOfSpaces
                "_".repeat(num)
            }
        }
    }
}
